import React, { useState, useEffect } from "react";
import { api } from "../services/api";
import { usePageMeta } from "../hooks/usePageMeta";

const SignIn: React.FC = () => {
  usePageMeta("Sign in — Ivy");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState<"microsoft" | "manual" | "hybrid">(
    "microsoft"
  );
  const [manualAuthEnabled, setManualAuthEnabled] = useState(false);
  const [microsoftAuthEnabled, setMicrosoftAuthEnabled] = useState(true);
  const [showManualAuth, setShowManualAuth] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Manual auth form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    // Check auth status to get auth mode information
    const checkAuthMode = async () => {
      try {
        const response = await api.get("/api/auth/status");
        setAuthMode(response.data.authMode || "microsoft");
        setManualAuthEnabled(response.data.manualAuthEnabled || false);
        setMicrosoftAuthEnabled(response.data.microsoftAuthEnabled !== false);
      } catch (error) {
        console.error("Error checking auth mode:", error);
      }
    };
    checkAuthMode();
  }, []);

  const handleMicrosoftSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get the frontend URL to pass to the backend
      const frontendUrl = api.getFrontendUrl();

      // Redirect to backend sign-in endpoint, passing frontend URL for the callback
      window.location.href = `${api.getBaseUrl()}/api/auth/sign-in?redirect_uri=${encodeURIComponent(
        frontendUrl
      )}`;

      console.log("Redirecting to Microsoft sign-in page...");
    } catch (error) {
      console.error("Microsoft sign in error:", error);
      setError("Failed to sign in with Microsoft. Please try again.");
      setIsLoading(false);
    }
  };

  const handleManualAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password || (isRegistering && !name)) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const endpoint = isRegistering
        ? "/api/auth/manual/register"
        : "/api/auth/manual/login";
      const payload = isRegistering
        ? { email, password, name }
        : { email, password };

      const response = await api.post(endpoint, payload);

      if (response.data.success) {
        // Redirect to dashboard or reload page
        window.location.reload();
      } else {
        setError(response.data.error || "Authentication failed");
      }
    } catch (error: any) {
      console.error("Manual auth error:", error);
      setError(
        error.response?.data?.error ||
          "Authentication failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setShowManualAuth(!showManualAuth);
    setError(null);
    setEmail("");
    setPassword("");
    setName("");
    setIsRegistering(false);
    setShowForgotPassword(false);
    setResetEmailSent(false);
    setOtpCode("");
    setOtpVerified(false);
    setAccessToken("");
    setNewPassword("");
    setConfirmNewPassword("");
    setSuccessMessage("");
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post("/api/auth/manual/forgot-password", {
        email,
      });

      if (response.data.success) {
        setResetEmailSent(true);
        setError(null);
      } else {
        setError(response.data.error || "Failed to send reset code");
      }
    } catch (error: any) {
      console.error("Forgot password error:", error);
      setError(
        error.response?.data?.error ||
          "Failed to send reset code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode || otpCode.length !== 6) {
      setError("Please enter the 6-digit code from your email");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post("/api/auth/manual/verify-otp", {
        email,
        otp_code: otpCode,
      });

      if (response.data.success) {
        setAccessToken(response.data.access_token);
        setOtpVerified(true);
        setError(null);
      } else {
        setError(response.data.error || "Invalid code");
      }
    } catch (error: any) {
      console.error("OTP verification error:", error);
      setError(
        error.response?.data?.error ||
          "Invalid or expired code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post("/api/auth/manual/reset-password", {
        password: newPassword,
        access_token: accessToken,
      });

      if (response.data.success) {
        setSuccessMessage(
          "Password reset successful! You can now sign in with your new password."
        );
        // Reset all forgot password states after 3 seconds
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmailSent(false);
          setOtpVerified(false);
          setOtpCode("");
          setNewPassword("");
          setConfirmNewPassword("");
          setAccessToken("");
          setSuccessMessage("");
        }, 3000);
      } else {
        setError(response.data.error || "Password reset failed");
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError(
        error.response?.data?.error ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-[#FAF9F5] font-schibsted px-4">
      <div className="max-w-md w-full bg-white rounded-lg p-8 border border-[#E5E1D6] shadow-[0_1px_2px_rgba(31,40,35,0.06),0_8px_24px_rgba(31,40,35,0.05)]">
        <div className="text-center mb-8">
          <img
            src="/Ivy-logo.png"
            alt=""
            className="h-10 w-auto mx-auto mb-4"
          />
          <h1 className="font-spectral font-medium text-3xl text-[#1F2823] mb-2">
            Welcome to <span className="text-[#0F766E]">Ivy</span>
          </h1>
          <p className="text-[#5B655E]">
            Track and manage your expert interviews with ease
          </p>
        </div>

        {/* Learn About Ivy Banner */}
        <div className="mb-6 p-4 bg-[#F2F0E9] border border-[#E5E1D6] rounded-md">
          <p className="text-sm font-medium text-[#1F2823] mb-1">New to Ivy?</p>
          <a
            href="/"
            className="inline-flex items-center text-sm font-medium text-[#0F766E] hover:text-[#0B5D57] underline"
          >
            Learn about the platform
            <svg
              className="ml-1 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Auth Mode Toggle */}
        {authMode === "hybrid" && (
          <div className="mb-6 flex rounded-md bg-[#F2F0E9] p-1">
            <button
              onClick={() => setShowManualAuth(false)}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                !showManualAuth
                  ? "bg-white text-[#1F2823] shadow-sm"
                  : "text-[#5B655E] hover:text-[#1F2823]"
              }`}
            >
              Microsoft
            </button>
            <button
              onClick={() => setShowManualAuth(true)}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                showManualAuth
                  ? "bg-white text-[#1F2823] shadow-sm"
                  : "text-[#5B655E] hover:text-[#1F2823]"
              }`}
            >
              Email
            </button>
          </div>
        )}

        {/* Microsoft Auth */}
        {!showManualAuth && microsoftAuthEnabled && (
          <div>
            <button
              onClick={handleMicrosoftSignIn}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 rounded-md text-sm font-medium text-white bg-[#0F766E] hover:bg-[#0B5D57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                "Sign in with Microsoft"
              )}
            </button>

            <div className="mt-6 text-center text-sm text-[#8A938C]">
              <p>
                Sign in with your Microsoft account to access your interview
                data
              </p>
            </div>
          </div>
        )}

        {/* Manual Auth */}
        {(showManualAuth || !microsoftAuthEnabled) && manualAuthEnabled && (
          <div>
            {/* Forgot Password Form - OTP Based */}
            {showForgotPassword ? (
              <div>
                {/* Success Message */}
                {successMessage ? (
                  <div className="p-4 bg-[#F2F0E9] border border-[#0F766E]/30 rounded-md">
                    <p className="text-sm font-medium text-[#0F766E]">
                      {successMessage}
                    </p>
                  </div>
                ) : otpVerified ? (
                  /* Step 3: Enter New Password */
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="text-center mb-4">
                      <h2 className="font-spectral text-xl font-medium text-[#1F2823]">
                        Set New Password
                      </h2>
                      <p className="text-sm text-[#5B655E] mt-1">
                        Code verified! Enter your new password.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="new-password"
                        className="block text-sm font-medium text-[#1F2823]"
                      >
                        New Password
                      </label>
                      <input
                        type="password"
                        id="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                        required
                        minLength={6}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-new-password"
                        className="block text-sm font-medium text-[#1F2823]"
                      >
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        id="confirm-new-password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                        required
                        minLength={6}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex justify-center items-center py-3 px-4 rounded-md text-sm font-medium text-white bg-[#0F766E] hover:bg-[#0B5D57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </form>
                ) : resetEmailSent ? (
                  /* Step 2: Enter OTP Code */
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="text-center mb-4">
                      <h2 className="font-spectral text-xl font-medium text-[#1F2823]">
                        Enter Verification Code
                      </h2>
                      <p className="text-sm text-[#5B655E] mt-1">
                        We sent a 6-digit code to <strong>{email}</strong>
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="otp-code"
                        className="block text-sm font-medium text-[#1F2823]"
                      >
                        6-Digit Code
                      </label>
                      <input
                        type="text"
                        id="otp-code"
                        value={otpCode}
                        onChange={(e) =>
                          setOtpCode(
                            e.target.value.replace(/\D/g, "").slice(0, 6)
                          )
                        }
                        className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823] text-center text-2xl tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || otpCode.length !== 6}
                      className="w-full flex justify-center items-center py-3 px-4 rounded-md text-sm font-medium text-white bg-[#0F766E] hover:bg-[#0B5D57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading ? "Verifying..." : "Verify Code"}
                    </button>

                    <div className="mt-4 text-center space-y-2">
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={isLoading}
                        className="text-sm text-[#0F766E] hover:text-[#0B5D57] underline"
                      >
                        Resend Code
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetEmailSent(false);
                          setOtpCode("");
                          setEmail("");
                          setError(null);
                        }}
                        className="text-sm text-[#5B655E] hover:text-[#1F2823] underline block w-full"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Step 1: Enter Email */
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="text-center mb-4">
                      <h2 className="font-spectral text-xl font-medium text-[#1F2823]">
                        Reset Password
                      </h2>
                      <p className="text-sm text-[#5B655E] mt-1">
                        Enter your email and we'll send you a verification code
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="reset-email"
                        className="block text-sm font-medium text-[#1F2823]"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="reset-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex justify-center items-center py-3 px-4 rounded-md text-sm font-medium text-white bg-[#0F766E] hover:bg-[#0B5D57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading ? "Sending..." : "Send Verification Code"}
                    </button>

                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setEmail("");
                          setError(null);
                        }}
                        className="text-sm text-[#0F766E] hover:text-[#0B5D57] underline"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              /* Regular Login/Register Form */
              <>
                <form onSubmit={handleManualAuth} className="space-y-4">
                  {isRegistering && (
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-[#1F2823]"
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[#1F2823]"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-[#1F2823]"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-[#E5E1D6] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0F766E] focus:border-[#0F766E] bg-white text-[#1F2823]"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center items-center py-3 px-4 rounded-md text-sm font-medium text-white bg-[#0F766E] hover:bg-[#0B5D57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {isRegistering ? "Registering..." : "Signing in..."}
                      </>
                    ) : isRegistering ? (
                      "Register"
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center space-y-2">
                  <button
                    onClick={() => setIsRegistering(!isRegistering)}
                    className="text-sm text-[#0F766E] hover:text-[#0B5D57] underline block w-full"
                  >
                    {isRegistering
                      ? "Already have an account? Sign in"
                      : "Need an account? Register"}
                  </button>
                  {!isRegistering && (
                    <button
                      onClick={() => {
                        setShowForgotPassword(true);
                        setError(null);
                      }}
                      className="text-sm text-[#5B655E] hover:text-[#1F2823] underline block w-full"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              </>
            )}

            {showManualAuth && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Manual mode:</span> Calendar
                  sync is disabled. You'll need to add interviews manually.
                </p>
              </div>
            )}

            <div className="mt-6 text-center text-sm text-[#8A938C]">
              <p>
                {isRegistering
                  ? "Create an account to get started"
                  : "Sign in with your email and password"}
              </p>
            </div>
          </div>
        )}

        {/* Toggle between auth methods for hybrid mode */}
        {authMode === "hybrid" && (
          <div className="mt-6 text-center">
            <button
              onClick={toggleAuthMode}
              className="text-sm text-[#5B655E] hover:text-[#1F2823] underline"
            >
              {showManualAuth
                ? "Use Microsoft instead"
                : "Use email/password instead"}
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 flex items-center gap-4 text-xs text-[#8A938C]">
        <a
          href="/privacy"
          className="hover:text-[#0F766E] transition-colors"
        >
          Privacy Policy
        </a>
        <span>·</span>
        <a
          href="/terms"
          className="hover:text-[#0F766E] transition-colors"
        >
          Terms of Service
        </a>
      </div>
    </div>
  );
};

export default SignIn;
